import { useState, useMemo } from "react";
import {
  Trophy, Calendar, Clock, Lightning, Target,
  CheckCircle, X
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MatchCard from "@/components/results/MatchCard";
import RescheduleMatchDialog from "@/components/tournament/RescheduleMatchDialog";
import ShareMatchDialog from "@/components/match/ShareMatchDialog";
import RescheduleRequestsPanel from "@/components/admin/RescheduleRequestsPanel";

export default function ResultsPanel({
  tournaments, players, selectedResultTournament, tournamentMatches,
  resultDate, dayMatches, fetchTournamentMatchesForResult, fetchDayMatches, fetchData
}) {
  const [viewMode, setViewMode] = useState("tournament"); // "tournament" or "date"
  const [playerFilter, setPlayerFilter] = useState("all");
  const [reschedulingMatch, setReschedulingMatch] = useState(null);
  const [sharingMatch, setSharingMatch] = useState(null);

  const onRefresh = () => {
    if (viewMode === "tournament" && selectedResultTournament) {
      fetchTournamentMatchesForResult(selectedResultTournament);
    }
    if (viewMode === "date" && resultDate) {
      fetchDayMatches(resultDate);
    }
    fetchData();
  };

  const activeTournaments = useMemo(() => tournaments.filter(t => t.status !== "completed"), [tournaments]);
  const completedTournaments = useMemo(() => tournaments.filter(t => t.status === "completed"), [tournaments]);
  const rawMatchList = viewMode === "tournament" ? tournamentMatches : dayMatches;
  const matchList = useMemo(
    () => playerFilter === "all"
      ? rawMatchList
      : rawMatchList.filter(m => m.player1_id === playerFilter || m.player2_id === playerFilter),
    [rawMatchList, playerFilter]
  );

  // Players that actually appear in the current match set — makes the dropdown
  // useful (avoids showing every player in the system if only a few are playing)
  const filterablePlayers = useMemo(() => {
    const ids = new Set();
    rawMatchList.forEach(m => {
      if (m.player1_id) ids.add(m.player1_id);
      if (m.player2_id) ids.add(m.player2_id);
    });
    return players
      .filter(p => ids.has(p.id))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [rawMatchList, players]);

  const filteredPlayerName = playerFilter === "all"
    ? null
    : (players.find(p => p.id === playerFilter)?.name || "Selected player");

  // Group matches by status for tournament view (memoised to avoid re-filtering each render)
  const { scheduledMatches, liveMatches, completedMatches, scheduledByDate } = useMemo(() => {
    const scheduled = matchList.filter(m => m.status === "scheduled");
    const live = matchList.filter(m => m.status === "live");
    const completed = matchList.filter(m => m.status === "completed" || m.status === "walkover");
    const byDate = {};
    for (const m of scheduled) {
      const date = m.scheduled_date || "Unscheduled";
      if (!byDate[date]) byDate[date] = [];
      byDate[date].push(m);
    }
    // pre-sort each date bucket by scheduled_time
    for (const date of Object.keys(byDate)) {
      byDate[date].sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
    }
    return { scheduledMatches: scheduled, liveMatches: live, completedMatches: completed, scheduledByDate: byDate };
  }, [matchList]);

  const sortedScheduledDates = useMemo(
    () => Object.entries(scheduledByDate).sort(([a], [b]) => a.localeCompare(b)),
    [scheduledByDate]
  );

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "Unscheduled") return "Unscheduled";
    try {
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    } catch { return dateStr; }
  };

  const totalMatches = matchList.length;
  const completedCount = completedMatches.length;

  return (
    <div className="space-y-4">
      {/* Player reschedule requests (admin inbox) */}
      <RescheduleRequestsPanel onReschedule={onRefresh} />

      {/* Top Controls */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
        {/* View toggle */}
        <div className="flex bg-muted/50 rounded-lg p-1 gap-1">
          <Button
            size="sm" variant={viewMode === "tournament" ? "default" : "ghost"}
            onClick={() => setViewMode("tournament")}
            className="h-8 text-xs"
            data-testid="view-by-tournament"
          >
            <Trophy className="w-3.5 h-3.5 mr-1" />By Tournament
          </Button>
          <Button
            size="sm" variant={viewMode === "date" ? "default" : "ghost"}
            onClick={() => setViewMode("date")}
            className="h-8 text-xs"
            data-testid="view-by-date"
          >
            <Calendar className="w-3.5 h-3.5 mr-1" />By Date
          </Button>
        </div>

        {/* Selector */}
        {viewMode === "tournament" ? (
          <div className="flex-1 w-full md:max-w-sm">
            <Select value={selectedResultTournament} onValueChange={v => fetchTournamentMatchesForResult(v)}>
              <SelectTrigger className="bg-card border-border" data-testid="select-tournament-results">
                <SelectValue placeholder="Select tournament..." />
              </SelectTrigger>
              <SelectContent>
                {activeTournaments.length > 0 && (
                  <>
                    <SelectItem value="_label_active" disabled className="text-xs text-muted-foreground font-semibold">Active Tournaments</SelectItem>
                    {activeTournaments.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.year ? ` (${t.year})` : ""}</SelectItem>)}
                  </>
                )}
                {completedTournaments.length > 0 && (
                  <>
                    <SelectItem value="_label_completed" disabled className="text-xs text-muted-foreground font-semibold">Completed</SelectItem>
                    {completedTournaments.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="w-full md:w-48">
            <Input
              type="date" value={resultDate}
              onChange={e => fetchDayMatches(e.target.value)}
              className="bg-card border-border"
              data-testid="select-date-results"
            />
          </div>
        )}

        {/* Player filter */}
        {filterablePlayers.length > 0 && (
          <div className="w-full md:w-56 flex items-center gap-1">
            <Select value={playerFilter} onValueChange={setPlayerFilter}>
              <SelectTrigger className="bg-card border-border" data-testid="filter-by-player">
                <SelectValue placeholder="Filter by player..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All players</SelectItem>
                {filterablePlayers.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {playerFilter !== "all" && (
              <Button
                size="sm" variant="ghost"
                className="h-8 w-8 p-0"
                onClick={() => setPlayerFilter("all")}
                data-testid="clear-player-filter"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        )}

        {/* Stats summary */}
        {totalMatches > 0 && (
          <div className="flex items-center gap-3 ml-auto text-xs text-muted-foreground">
            <span>{completedCount}/{totalMatches} completed</span>
            {liveMatches.length > 0 && (
              <Badge className="bg-secondary/20 text-secondary border-secondary/30 text-[10px]">
                <Lightning className="w-3 h-3 mr-0.5" weight="fill" />{liveMatches.length} Live
              </Badge>
            )}
            {scheduledMatches.length > 0 && (
              <span>{scheduledMatches.length} upcoming</span>
            )}
          </div>
        )}
      </div>

      {/* Match List */}
      {totalMatches === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Target className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">
              {filteredPlayerName
                ? `No matches for ${filteredPlayerName} in this ${viewMode === "tournament" ? "tournament" : "date"}`
                : viewMode === "tournament"
                  ? (selectedResultTournament ? "No matches for this tournament yet" : "Select a tournament to view matches")
                  : "No matches on this date"}
            </p>
            {filteredPlayerName && (
              <Button
                size="sm" variant="outline" className="mt-3"
                onClick={() => setPlayerFilter("all")}
                data-testid="empty-clear-filter"
              >
                Show all players
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Live Matches */}
          {liveMatches.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Lightning className="w-4 h-4 text-secondary" weight="fill" />
                <h3 className="text-sm font-semibold text-secondary uppercase tracking-wider">Live Now</h3>
              </div>
              <div className="space-y-2">
                {liveMatches.map(m => (
                  <MatchCard key={m.id} match={m} players={players} onRefresh={onRefresh} onReschedule={setReschedulingMatch} onShare={setSharingMatch} />
                ))}
              </div>
            </div>
          )}

          {/* Scheduled Matches — grouped by date */}
          {scheduledMatches.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Upcoming ({scheduledMatches.length})
                </h3>
              </div>
              {sortedScheduledDates.map(([date, matches]) => (
                <div key={date} className="mb-4">
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">{formatDate(date)}</span>
                    <span className="text-[10px] text-muted-foreground">({matches.length} {matches.length === 1 ? "match" : "matches"})</span>
                  </div>
                  <div className="space-y-2">
                    {matches.map(m => (
                      <MatchCard key={m.id} match={m} players={players} onRefresh={onRefresh} onReschedule={setReschedulingMatch} onShare={setSharingMatch} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Completed Matches */}
          {completedMatches.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-4 h-4 text-primary" weight="fill" />
                <h3 className="text-sm font-semibold text-primary uppercase tracking-wider">
                  Completed ({completedMatches.length})
                </h3>
              </div>
              <div className="space-y-2">
                {completedMatches.map(m => (
                  <MatchCard key={m.id} match={m} players={players} onRefresh={onRefresh} onReschedule={setReschedulingMatch} onShare={setSharingMatch} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <RescheduleMatchDialog
        open={!!reschedulingMatch}
        onOpenChange={(v) => { if (!v) setReschedulingMatch(null); }}
        match={reschedulingMatch ? {
          ...reschedulingMatch,
          player1_name: reschedulingMatch.player1_name || players.find(p => p.id === reschedulingMatch.player1_id)?.name,
          player2_name: reschedulingMatch.player2_name || players.find(p => p.id === reschedulingMatch.player2_id)?.name,
        } : null}
        tournamentId={reschedulingMatch?.tournament_id}
        onChanged={onRefresh}
      />

      <ShareMatchDialog
        open={!!sharingMatch}
        onOpenChange={(v) => { if (!v) setSharingMatch(null); }}
        match={sharingMatch ? {
          ...sharingMatch,
          player1_name: sharingMatch.player1_name || players.find(p => p.id === sharingMatch.player1_id)?.name,
          player2_name: sharingMatch.player2_name || players.find(p => p.id === sharingMatch.player2_id)?.name,
          tournament_name: sharingMatch.tournament_name
            || tournaments.find(t => t.id === sharingMatch.tournament_id)?.name,
        } : null}
      />
    </div>
  );
}
